/**
 * CloudWatch Logs Viewer Component (MVP)
 * Displays and filters application logs from PM2 and CloudWatch
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Tab,
  Tabs,
  Skeleton,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as DebugIcon,
  Security as SecurityIcon,
  Assessment as StatsIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { LOG_PRESETS, LogToken } from '../common/timeRange';

interface LogEntry {
  timestamp: string;
  severity: 'error' | 'warning' | 'info' | 'debug';
  service: string;
  message: string;
  metadata?: any;
}

interface LogStatistics {
  total: number;
  bySeverity: {
    error?: number;
    warning?: number;
    info?: number;
    debug?: number;
  };
  byService: { [key: string]: number };
  errorRate: string;
  warningRate: string;
}

interface CloudWatchAlarm {
  name: string;
  state: 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';
  metricName: string;
  namespace: string;
  threshold: number;
  comparisonOperator: string;
  reason: string;
  lastUpdate: string;
}

const CloudWatchLogsViewer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [alarms, setAlarms] = useState<CloudWatchAlarm[]>([]);
  const [stats, setStats] = useState<LogStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  // Log windows reason in minutes, so this screen renders the shared pill
  // control over LOG_PRESETS. The auto-refresh Select below is a CADENCE, not a
  // range — it stays a Select.
  const [timeRange, setTimeRange] = useTimeRangeParam<LogToken>(
    { type: '1h' },
    { presets: LOG_PRESETS }
  );
  const [limit, setLimit] = useState(50);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(180000); // 3 minutes default

  // Tab definitions
  const tabs = [
    { label: 'Recent Logs', value: 'recent', icon: <InfoIcon /> },
    { label: 'Alarms', value: 'alarms', icon: <WarningIcon />, color: 'warning' },
    { label: 'Errors', value: 'errors', icon: <ErrorIcon />, color: 'error' },
    { label: 'Security', value: 'security', icon: <SecurityIcon />, color: 'warning' },
    { label: 'Statistics', value: 'stats', icon: <StatsIcon /> },
  ];

  // Fetch logs based on active tab
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const activeTabValue = tabs[activeTab].value;
      let endpoint = '';
      let params: any = { timeRange: timeRange.type, limit };

      let response: any;

      switch (activeTabValue) {
        case 'recent':
          params.severity = severityFilter !== 'all' ? severityFilter : undefined;
          params.service = serviceFilter !== 'all' ? serviceFilter : undefined;
          params.search = searchTerm || undefined;
          response = await apiService.getCloudWatchLogsRecent(params);
          break;

        case 'alarms':
          response = await apiService.getCloudWatchAlarms(params);
          break;

        case 'errors':
          params.service = serviceFilter !== 'all' ? serviceFilter : undefined;
          response = await apiService.getCloudWatchLogsErrors(params);
          break;

        case 'security':
          response = await apiService.getCloudWatchLogsSecurity(params);
          break;

        case 'stats':
          response = await apiService.getCloudWatchLogsStats(params);
          break;
      }

      // Normalize: fetchWithAuth returns { data: body } when body has no .data; some callers wrap again
      const payload = response?.data ?? response;
      const ok = payload?.success !== false;

      if (ok) {
        setEmptyMessage(payload?.message ?? null);
        if (activeTabValue === 'stats') {
          setStats(payload?.statistics ?? null);
          setLogs([]);
          setAlarms([]);
        } else if (activeTabValue === 'alarms') {
          setAlarms(response.data.alarms || response.data.data || []);
          setLogs([]);
          setStats(null);
        } else {
          const list = payload?.logs;
          setLogs(Array.isArray(list) ? list : []);
          setStats(null);
          setAlarms([]);
        }
      } else {
        throw new Error(payload?.error || 'Failed to fetch logs');
      }
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      setError(err.message || 'Failed to fetch logs');
      dispatch(addNotification({
        type: 'error',
        message: `Failed to fetch logs: ${err.message}`,
      }));
    } finally {
      setLoading(false);
    }
  }, [activeTab, timeRange, limit, severityFilter, serviceFilter, searchTerm, dispatch]);

  // Initial load
  useEffect(() => {
    fetchLogs();
  }, [activeTab, timeRange, severityFilter, serviceFilter]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLogs();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchLogs]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchLogs();
  };

  // Handle search
  const handleSearch = () => {
    fetchLogs();
  };

  // Get severity color
  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error': return <ErrorIcon fontSize="small" />;
      case 'warning': return <WarningIcon fontSize="small" />;
      case 'info': return <InfoIcon fontSize="small" />;
      case 'debug': return <DebugIcon fontSize="small" />;
      default: return null;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  // Render loading state
  if (loading && logs.length === 0 && !stats) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📋 CloudWatch Logs
          </Typography>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📋 CloudWatch Logs
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Time Range Selector */}
            <TimeRangeFilter
              value={timeRange}
              onChange={setTimeRange}
              presets={LOG_PRESETS}
              hideCustom
            />

            {/* Auto-refresh Interval */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={autoRefresh ? refreshInterval : 'manual'}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'manual') {
                    setAutoRefresh(false);
                  } else {
                    setAutoRefresh(true);
                    setRefreshInterval(Number(value));
                  }
                }}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="10000">10 sec</MenuItem>
                <MenuItem value="30000">30 sec</MenuItem>
                <MenuItem value="60000">1 min</MenuItem>
                <MenuItem value="120000">2 min</MenuItem>
                <MenuItem value="300000">5 min</MenuItem>
                <MenuItem value="600000">10 min</MenuItem>
              </Select>
            </FormControl>

            {/* Refresh Button */}
            <Tooltip title="Refresh logs">
              <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.value}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>

        {/* Filters (only for Recent Logs tab) */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 250 }}
            />

            {/* Severity Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                label="Severity"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="debug">Debug</MenuItem>
              </Select>
            </FormControl>

            {/* Service Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Service</InputLabel>
              <Select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                label="Service"
              >
                <MenuItem value="all">All Services</MenuItem>
                <MenuItem value="lifescribe-admin">Admin Dashboard</MenuItem>
                <MenuItem value="lifescribe-main">Main API</MenuItem>
                <MenuItem value="lifescribe-transcription">Transcription</MenuItem>
                <MenuItem value="lifescribe-payment">Payment</MenuItem>
              </Select>
            </FormControl>

            {/* Limit */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Limit</InputLabel>
              <Select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                label="Limit"
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>

            {/* Search Button */}
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Box>
        )}

        {/* Statistics View */}
        {activeTab === 4 && stats && (
          <Box>
            {stats.total === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {emptyMessage || 'No log entries. This viewer reads from local log files; in production, view log groups in AWS CloudWatch (e.g. /ecs/lifescribe-main).'}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                📊 Showing statistics for {stats.total} log entries over the last {timeRange.type}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Total Logs</Typography>
                  <Typography variant="h4">{stats.total}</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Error Rate</Typography>
                  <Typography variant="h4" color="error.main">{stats.errorRate}%</Typography>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Warning Rate</Typography>
                  <Typography variant="h4" color="warning.main">{stats.warningRate}%</Typography>
                </CardContent>
              </Card>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>By Severity</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {Object.entries(stats.bySeverity).map(([severity, count]) => (
                <Chip
                  key={severity}
                  label={`${severity}: ${count}`}
                  color={getSeverityColor(severity)}
                  icon={getSeverityIcon(severity)}
                />
              ))}
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>By Service</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(stats.byService).map(([service, count]) => (
                <Chip
                  key={service}
                  label={`${service}: ${count}`}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Alarms Table */}
        {activeTab === 1 && !loading && (
          <Card>
            <CardContent>
              {alarms.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <WarningIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6">No CloudWatch Alarms</Typography>
                  <Typography variant="body2">
                    All systems nominal or no alarms configured yet.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Alarm Name</strong></TableCell>
                        <TableCell><strong>State</strong></TableCell>
                        <TableCell><strong>Metric</strong></TableCell>
                        <TableCell><strong>Namespace</strong></TableCell>
                        <TableCell><strong>Threshold</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Last Update</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alarms.map((alarm, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{alarm.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={alarm.state}
                              size="small"
                              color={
                                alarm.state === 'ALARM' ? 'error' :
                                alarm.state === 'OK' ? 'success' :
                                'warning'
                              }
                            />
                          </TableCell>
                          <TableCell>{alarm.metricName}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {alarm.namespace}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {alarm.comparisonOperator.replace('Than', ' ')} {alarm.threshold}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                              {alarm.reason || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(alarm.lastUpdate).toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Logs Table */}
        {activeTab !== 1 && activeTab !== 4 && (
          <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '150px' }}>Timestamp</TableCell>
                  <TableCell sx={{ width: '100px' }}>Severity</TableCell>
                  <TableCell sx={{ width: '150px' }}>Service</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" sx={{ mb: 1 }}>
                        No logs found for the selected filters
                      </Typography>
                      <Alert severity="info" sx={{ maxWidth: 560, mx: 'auto', textAlign: 'left', mt: 1 }}>
                        {emptyMessage || 'This viewer reads from local log files. In production, application logs are in AWS CloudWatch (e.g. /ecs/lifescribe-main). Use the AWS Console or CLI to view them.'}
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={log.severity}
                          color={getSeverityColor(log.severity)}
                          icon={getSeverityIcon(log.severity)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {log.service}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {log.message.length > 200
                          ? log.message.substring(0, 200) + '...'
                          : log.message}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Loading logs...
            </Typography>
          </Box>
        )}

        {/* Footer Info */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {logs.length} log entries • Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Typography>
          {autoRefresh && (
            <Chip
              label={`Refreshing every ${refreshInterval / 1000}s`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CloudWatchLogsViewer;
